/**
 * Селектор подразделения для консоли подтверждения (задача 22 §4.8).
 *
 * Виден админу (подмена любого мастера — выбор любого юнита из scope) и
 * мультиюнит-лиду (выбор среди своих прямых ведомых). Single-unit
 * лид селектора не видит — бэк резолвит его юнит сам (этот компонент в
 * консоли просто не рендерится).
 *
 * Фильтруемый `<Combobox>` (`patterns.md §Фильтруемый комбобокс`, задача 81
 * §4.2): опция = `Unit.name` (технический `code` не показываем —
 * `project_ui_hide_technical_ids`), ФИО мастера (`Unit.lead?.name`) — вторичной
 * строкой-подсказкой. Очистки нет — подразделение всегда выбрано.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/ui/combobox";
import { Surface } from "@/components/ui/surface";
import { Switch } from "@/components/ui/switch";
import type { Unit } from "@/api/units";

interface UnitPickerProps {
  units: Unit[];
  selectedUnitId: number | null;
  onChange: (unitId: number) => void;
  showLabel?: boolean;
}

export function UnitPicker({
  units,
  selectedUnitId,
  onChange,
  showLabel = true,
}: UnitPickerProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className={showLabel ? "flex flex-col gap-1 sm:w-72" : "sm:w-72"}>
      {showLabel && (
        <span className="text-xs text-[var(--fg-3)]">{t("timeApproval.unitPicker.label")}</span>
      )}
      <Combobox
        data-testid="approval-unit-trigger"
        options={units
          .filter((u) => u.id != null)
          .map((u) => ({
            value: String(u.id),
            label: u.name,
            hint: u.lead?.display_name
              ? t("timeApproval.unitPicker.leadHint", { name: u.lead.display_name })
              : undefined,
          }))}
        value={selectedUnitId != null ? String(selectedUnitId) : null}
        onChange={(v) => {
          if (v !== null) onChange(Number(v));
        }}
        placeholder={t("timeApproval.unitPicker.placeholder")}
        searchPlaceholder={t("common.combobox.searchPlaceholder")}
        emptyText={t("common.combobox.empty")}
      />
    </div>
  );
}

interface ApprovalUnitPickerProps extends UnitPickerProps {
  visible: boolean;
  includeChildren?: boolean;
  onIncludeChildrenChange?: (checked: boolean) => void;
  showIncludeChildren?: boolean;
}

export function ApprovalUnitPicker({
  visible,
  includeChildren = false,
  onIncludeChildrenChange = () => undefined,
  showIncludeChildren = false,
  ...pickerProps
}: ApprovalUnitPickerProps): React.ReactElement | null {
  const { t } = useTranslation();
  const labelId = React.useId();
  if (!visible && !showIncludeChildren) return null;

  return (
    <Surface className="p-3">
      {visible && (
        <span id={labelId} className="text-xs text-[var(--fg-3)]">
          {t("timeApproval.unitPicker.label")}
        </span>
      )}
      <div
        role="group"
        aria-labelledby={visible ? labelId : undefined}
        aria-label={visible ? undefined : t("timeApproval.unitPicker.label")}
        className={
          visible ? "mt-1 flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2"
        }
      >
        {visible && <UnitPicker {...pickerProps} showLabel={false} />}
        {showIncludeChildren && (
          <label className="flex min-h-7 items-center gap-2 text-sm">
            <Switch
              checked={includeChildren}
              disabled={pickerProps.selectedUnitId == null}
              onCheckedChange={onIncludeChildrenChange}
              data-testid="approval-include-children"
            />
            {t("units.detail.includeSubtree")}
          </label>
        )}
      </div>
    </Surface>
  );
}

interface ApprovalUnitSelectionSyncProps {
  selectedUnitId: number | null;
  nextUnitId: number | null | undefined;
  onUnitChange: (unitId: number | null) => void;
}

export function ApprovalUnitSelectionSync({
  selectedUnitId,
  nextUnitId,
  onUnitChange,
}: ApprovalUnitSelectionSyncProps): null {
  const requestKey =
    nextUnitId === undefined ? null : `${selectedUnitId ?? "none"}:${nextUnitId ?? "none"}`;
  const lastRequestRef = React.useRef<string | null>(null);
  const onUnitChangeRef = React.useRef(onUnitChange);

  React.useEffect(() => {
    onUnitChangeRef.current = onUnitChange;
  }, [onUnitChange]);

  React.useEffect(() => {
    if (requestKey === null || nextUnitId === undefined) {
      lastRequestRef.current = null;
      return;
    }
    if (lastRequestRef.current === requestKey) return;
    lastRequestRef.current = requestKey;
    onUnitChangeRef.current(nextUnitId);
  }, [nextUnitId, requestKey]);

  return null;
}
